/**
 * Reload once when a code-split chunk from an older build cannot be loaded (2026-10-08).
 *
 * After a deploy, a tab (or a cached index.html) still running the previous build asks for
 * chunk files that the new deployment no longer has. Cloudflare Pages answers a missing path
 * with the SPA's index.html (200, text/html), so the lazy route's import fails with "Failed to
 * fetch dynamically imported module" and the page shows the error boundary until the cache
 * expires. Vite reports every failed dynamic import as a cancellable `vite:preloadError`
 * event on window; reloading picks up the current build's index.html and chunk names.
 *
 * One reload per minute at most: a chunk that is genuinely broken in the current build must
 * reach the error boundary instead of looping. Storage that throws (private mode, blocked
 * site data) only loses the loop guard, so the reload is skipped rather than risked.
 */

const STORAGE_KEY = 'inferrex:stale-chunk-reload-at';
const RELOAD_WINDOW_MS = 60_000;

export interface StaleChunkReloadTarget {
  addEventListener(type: 'vite:preloadError', listener: (event: Event) => void): void;
  readonly sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
  readonly location: Pick<Location, 'reload'>;
}

export function reloadOnStaleChunk(target: StaleChunkReloadTarget = window, now: () => number = Date.now): void {
  target.addEventListener('vite:preloadError', (event) => {
    let last: number;
    try {
      last = Number(target.sessionStorage.getItem(STORAGE_KEY) ?? 0);
      if (now() - last < RELOAD_WINDOW_MS) return;
      target.sessionStorage.setItem(STORAGE_KEY, String(now()));
    } catch {
      return;
    }
    event.preventDefault();
    target.location.reload();
  });
}
